import React, { FunctionComponent, useCallback, useState } from "react";
import { Button, LinearProgress, TextField } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { Symbology } from "@scandit/web-datacapture-barcode";

import { emptyProduct } from "../../../../models/Product";
import * as ProductApi from "../../../../api/productApi";
import useStyles from "./styles";
import * as GTIN from "../../../../models/GTIN";
import * as Result from "../../../../result";
import { generateEAN } from "../../../statistics/shared/eanFunctions";
import { globalSettings } from "../../../../models/Settings";
import { useI18n } from "../../../../hooks/useI18n";
import { ScanditBarcodeScanner } from "../../../../components/ScanditBarcodeScanner";

interface Props {
  onScanned: (product: any, imageData: any) => void;
}

export const ScanNewProduct: FunctionComponent<Props> = ({ onScanned }) => {
  const classes = useStyles();
  const { t } = useI18n();

  const [state, setState] = useState({
    scanning: true,
    loadingProductInformation: false,
    gtin: "",
    lookupProductFailed: false,
    error: ""
  });

  const lookupProduct = useCallback(
    async (gtin: string) => {
      const trimmed = (gtin ?? "").trim();
      if (!trimmed) return;

      setState(s => ({
        ...s,
        loadingProductInformation: true,
        lookupProductFailed: false,
        gtin: trimmed,
        scanning: false,
        error: ""
      }));

      const response = await ProductApi.lookup(trimmed);

      if (response.type === "ERROR") {
        setState(s => ({
          ...s,
          loadingProductInformation: false,
          lookupProductFailed: true,
          error: response.details.detail ?? "",
          scanning: true
        }));
        return;
      }

      const value = response.value ?? {};
      const product = value.product;
      const imageData = value.imageData ?? {};

      setState(s => ({
        ...s,
        loadingProductInformation: false,
        scanning: true
      }));

      if (!product || Object.keys(value).length === 0) {
        onScanned({ ...emptyProduct(trimmed), gtin: trimmed }, {});
      } else {
        onScanned(product, imageData);
      }
    },
    [onScanned]
  );

  const onManualOk = useCallback(() => {
    const gtinParsed = GTIN.fromString(state.gtin ?? "");
    const val = Result.coerce(gtinParsed);

    if (!val) {
      setState(s => ({ ...s, error: t("common.messages.invalidGtin") }));
      return;
    }

    void lookupProduct(state.gtin);
  }, [lookupProduct, state.gtin, t]);

  const onGenerate = useCallback(async () => {
    const ean = await generateEAN();
    if (ean) {
      setState(s => ({
        ...s,
        gtin: ean,
        error: "",
        lookupProductFailed: false
      }));
    }
  }, []);

  return (
    <div
      style={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden"
      }}
    >
      {/* Scanner */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          position: "relative",
          overflow: "hidden",
          background: "#000"
        }}
      >
        <ScanditBarcodeScanner
          licenseKey={globalSettings.settings.scandit.scannerLicenseKey}
          libraryLocation="https://cdn.jsdelivr.net/npm/@scandit/web-datacapture-barcode@8.0.0/sdc-lib/"
          style={{ width: "100%", height: "100%" }}
          paused={state.loadingProductInformation}
          onScan={async (data: string, _symbology: Symbology) => {
            if (!data) return;
            await lookupProduct(data);
          }}
        />
      </div>

      {/* Footer */}
      <div
        style={{
          flex: "0 0 auto",
          backgroundColor: "#f9f9f9",
          borderTop: "1px solid #e0e0e0",
          padding: 16
        }}
      >
        {/* Row: input + OK */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 12,
            alignItems: "center"
          }}
        >
          <TextField
            size="small"
            fullWidth
            disabled={state.loadingProductInformation}
            className={classes.manualInput}
            error={state.error === t("common.messages.invalidGtin")}
            helperText={state.error ?? " "}
            type="number"
            label={t("common.formFields.manualInput")}
            variant="outlined"
            value={state.gtin ?? ""}
            inputProps={{ style: { fontSize: 16 } }}
            InputLabelProps={{ style: { fontSize: 16 } }}
            onChange={e =>
              setState(s => ({
                ...s,
                gtin: e.target.value,
                error: "",
                lookupProductFailed: false
              }))
            }
            onKeyDown={e => {
              if (e.key === "Enter") onManualOk();
            }}
          />

          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={state.loadingProductInformation}
            style={{
              height: 40,
              minWidth: 84,
              borderRadius: 10,
              textTransform: "none",
              whiteSpace: "nowrap"
            }}
            onClick={onManualOk}
          >
            {t("common.actions.ok")}
          </Button>
        </div>

        {/* Row: Generate barcode */}
        <div style={{ marginTop: 12 }}>
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            disableElevation
            disabled={state.loadingProductInformation}
            style={{
              height: 40,
              borderRadius: 10,
              textTransform: "none"
            }}
            onClick={onGenerate}
          >
            {t("common.generateBarcode")}
          </Button>
        </div>

        {state.loadingProductInformation && (
          <LinearProgress style={{ marginTop: 12, borderRadius: 2 }} />
        )}

        {state.lookupProductFailed && (
          <Alert
            severity="error"
            style={{ marginTop: 12, padding: "8px 12px" }}
          >
            {state.error}
          </Alert>
        )}
      </div>
    </div>
  );
};
